// VIMKUNYA▼ is a single-product, single-dose PGD. Black triangle product.
// Contraindicated in pregnancy and breastfeeding (exclusion, not caution).
// Single 0.8 mL IM dose. Co-administration not studied — advisory flagged
// at basket level in the co-admin matrix; not repeated as an exclusion or
// caution here.

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.CHIKUNGUNYA,
  displayName: "Chikungunya (VIMKUNYA\u25bc)",
  serviceType: "administration",
  minimumAgeYears: 12,
  indicationSummary:
    "Travelling to or a frequent traveller to areas with chikungunya virus transmission, as identified by NaTHNaC assessment.",

  brands: [
    {
      brandId: "vimkunya",
      brandName: "VIMKUNYA\u25bc",
      minimumAgeYears: 12,
      route: "IM",
      doseDescription: "0.8 mL single dose",
      administrationNotes:
        "Shake the vial to obtain a homogeneous white cloudy suspension. Inspect before use \u2014 do not administer if discoloured or if particulate matter is present. Attach a sterile needle before drawing up the dose. Do not mix with other vaccines in the same syringe. Administer by intramuscular injection into the deltoid muscle. Use within 4 hours of removal from 2\u20138\u00b0C refrigerated storage.",
    },
  ],

  schedules: [
    {
      scheduleId: "chikungunya-single-dose",
      label: "Single dose",
      offLabel: false,
      minimumAgeYears: 12,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 single dose",
        },
      ],
      completionNotes:
        "VIMKUNYA\u25bc is a single-dose schedule with no booster or follow-up dose under this PGD. Not all recipients are fully protected \u2014 advise the patient to continue mosquito-bite avoidance measures throughout their trip.",
    },
  ],

  capturesPriorDoseHistory: false,

  exclusions: [
    {
      checkId: "chikungunya-ex-under-12",
      question: "Is the patient under 12 years of age?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "VIMKUNYA\u25bc is not licensed for patients under 12 years of age. Do not proceed.",
      referralMessage: "Refer to the patient's GP or a paediatric travel health specialist.",
    },
    {
      checkId: "chikungunya-ex-hypersensitivity",
      question:
        "Does the patient have known hypersensitivity to the active substance or any excipient of VIMKUNYA\u25bc, including aluminium hydroxide?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "VIMKUNYA\u25bc is contraindicated in this patient. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "chikungunya-ex-prior-anaphylaxis",
      question:
        "Has the patient had a confirmed hypersensitivity reaction or anaphylaxis following a previous dose of VIMKUNYA\u25bc?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "A previous hypersensitivity reaction or anaphylaxis to VIMKUNYA\u25bc is a contraindication. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "chikungunya-ex-pregnant",
      question: "Is the patient pregnant?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "VIMKUNYA\u25bc is contraindicated in pregnancy. Do not proceed.",
      referralMessage: "Refer to the patient's GP or obstetrician for advice on alternative protective measures.",
    },
    {
      checkId: "chikungunya-ex-breastfeeding",
      question: "Is the patient breastfeeding?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "VIMKUNYA\u25bc is contraindicated in breastfeeding. Do not proceed.",
      referralMessage: "Refer to the patient's GP or obstetrician for advice on alternative protective measures.",
    },
    {
      checkId: "chikungunya-ex-neurological",
      question: "Does the patient have an evolving or unstable neurological condition?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Vaccination should be deferred until the neurological condition is stable or resolved.",
      referralMessage: "Refer to the patient's GP or neurologist. Rebook when the condition is stable.",
    },
  ],

  cautions: [
    {
      checkId: "chikungunya-caut-bleeding",
      question: "Does the patient have a bleeding disorder or is the patient on anticoagulant therapy?",
      cautionMessage:
        "Administer by intramuscular injection with care. For patients on stable warfarin with INR within therapeutic range, IM injection may proceed using a fine-gauge needle with firm pressure applied for at least two minutes afterwards. Document the clinical decision.",
    },
    {
      checkId: "chikungunya-caut-immunosuppressed",
      question: "Is the patient immunosuppressed due to disease or treatment?",
      cautionMessage:
        "The safety and efficacy of VIMKUNYA\u25bc in immunosuppressed patients have not been formally assessed. The immune response may be reduced. Seek specialist advice before proceeding. If vaccination proceeds, counsel the patient that protection may be suboptimal.",
    },
    {
      checkId: "chikungunya-caut-elderly",
      question: "Is the patient aged 65 or over, particularly 75 or over?",
      cautionMessage:
        "Patients aged 65 and over, particularly those aged 75 and over, may have a reduced immune response to VIMKUNYA\u25bc. Emphasise the continued importance of mosquito-bite avoidance measures throughout the trip.",
    },
    {
      checkId: "chikungunya-caut-syncope",
      question: "Is there any history of syncope (fainting) following a previous injection or vaccination?",
      cautionMessage:
        "Syncope can occur following vaccination. Ensure the patient is seated or lying down during and for the duration of the observation period after administration.",
    },
  ],

  supplyGates: [],

  requiresICVP: false,
  requiresLariamCard: false,
  // VIMKUNYA is a black triangle product under additional monitoring.
  requiresYellowCardDiscussion: true,
  requiresCHISNotificationUnder19: true,
  requiresGPNotification: true,
  standardObservationMinutes: 15,
  extendedObservationMinutes: 30,

  patientAdvicePoints: [
    "This vaccine protects against chikungunya virus only. Not all recipients are fully protected \u2014 mosquito-bite avoidance remains essential throughout your trip.",
    "Continue to use mosquito-bite avoidance measures: apply insect repellent containing DEET to exposed skin, wear long-sleeved clothing and long trousers, use permethrin-treated clothing and bed nets, and avoid outdoor activity at peak mosquito biting times (dawn and dusk).",
    "Chikungunya is transmitted by Aedes mosquitoes, which bite during the day as well as at dawn and dusk. Daytime bite prevention is important.",
    "If you develop a sudden high fever with severe joint pain during or after travel in a chikungunya-risk area, seek medical attention and inform the clinician of your travel history and vaccination.",
    "Please report any suspected side effects to the MHRA via the Yellow Card scheme at yellowcard.mhra.gov.uk or by calling 0800 731 6789. VIMKUNYA\u25bc is a product under additional monitoring.",
  ],
});
